/**
 * Раскладка Russian ЙЦУКЕН (ANSI и ISO).
 *
 * Ё — отдельная клавиша и отдельный навык (решение AD-014): нормализация Е/Ё
 * допустима только на уровне словаря, но не физической тренировки.
 *
 * Расположение клавиш — данные, и у них есть источник (ТЗ FR-EN-45): отличия
 * macOS описаны по Unicode CLDR (`keyboards/osx`), а не по наблюдению за
 * чужой клавиатурой. Раскладка, списанная с одного устройства, врёт на любом
 * другом, и проверить это нечем.
 */

import type { Finger } from '@type2x/shared-contracts';

import { buildKeys, chars } from './build.js';
import type { KeyOverride, KeyboardLayout, PlatformOverrides } from './types.js';

const LP: Finger = 'left-pinky';
const LR: Finger = 'left-ring';
const LM: Finger = 'left-middle';
const LI: Finger = 'left-index';
const RI: Finger = 'right-index';
const RM: Finger = 'right-middle';
const RR: Finger = 'right-ring';
const RP: Finger = 'right-pinky';
const TH: Finger = 'right-thumb';

export const RU_JCUKEN: KeyboardLayout = {
  id: 'ru-jcuken',
  language: 'ru',
  platform: 'windows',
  homeRowAnchors: ['ф', 'ы', 'в', 'а', 'о', 'л', 'д', 'ж'],
  keys: buildKeys([
    {
      row: 'number',
      base: chars('ё1234567890-='),
      shifted: chars('Ё!"№;%:?*()_+'),
      fingers: [LP, LP, LR, LM, LI, LI, RI, RI, RM, RR, RP, RP, RP],
    },
    {
      row: 'top',
      base: chars('йцукенгшщзхъ\\'),
      shifted: chars('ЙЦУКЕНГШЩЗХЪ/'),
      fingers: [LP, LR, LM, LI, LI, RI, RI, RM, RR, RP, RP, RP, RP],
    },
    {
      row: 'home',
      base: chars('фывапролджэ'),
      shifted: chars('ФЫВАПРОЛДЖЭ'),
      fingers: [LP, LR, LM, LI, LI, RI, RI, RM, RR, RP, RP],
    },
    {
      row: 'bottom',
      base: chars('ячсмитьбю.'),
      shifted: chars('ЯЧСМИТЬБЮ,'),
      fingers: [LP, LR, LM, LI, LI, RI, RI, RM, RR, RP],
    },
    {
      row: 'space',
      base: [' '],
      shifted: [''],
      fingers: [TH],
    },
  ]),
};

/**
 * Отличия ЙЦУКЕН на macOS.
 *
 * Apple раскладывает русскую раскладку иначе, чем Windows и Linux: Ё уходит из
 * цифрового ряда на клавишу справа от Ъ, точка и запятая переезжают на Shift+7
 * и Shift+6, а клавиша справа от Ю даёт слэш и вопросительный знак. Звёздочки
 * и обратной косой черты в этой раскладке нет вовсе. У Apple есть и вариант
 * «Русская — ПК», повторяющий Windows; он описывается раскладкой Windows и
 * отдельного описания не требует.
 *
 * Источник — данные CLDR `keyboards/osx/ru-t-k0-osx.xml` (Unicode CLDR 43.1),
 * а не наблюдение за чужой клавиатурой: расположение клавиш — это данные, и у
 * них должен быть источник, как у частотных словарей (ТЗ §12).
 */
const RU_JCUKEN_MACOS: readonly KeyOverride[] = [
  // Ё уходит с цифрового ряда: на её месте — квадратные скобки.
  { row: 'number', column: 0, base: ']', shifted: '[' },
  { row: 'number', column: 4, base: '4', shifted: '%' },
  { row: 'number', column: 5, base: '5', shifted: ':' },
  { row: 'number', column: 6, base: '6', shifted: ',' },
  { row: 'number', column: 7, base: '7', shifted: '.' },
  { row: 'number', column: 8, base: '8', shifted: ';' },
  // …и появляется справа от Ъ, где у Windows обратная косая черта.
  { row: 'top', column: 12, base: 'ё', shifted: 'Ё' },
  // Точка с запятой уступают место слэшу и вопросительному знаку.
  { row: 'bottom', column: 9, base: '/', shifted: '?' },
];

/** Отличия ЙЦУКЕН по операционным системам. Linux совпадает с Windows. */
export const RU_JCUKEN_OVERRIDES: PlatformOverrides = { macos: RU_JCUKEN_MACOS };
